// TaxImpactPanel — Phase 7. Shows the country's rental income tax burden,
// after-tax cash flow, after-tax CoC, and a side-by-side pre-tax vs post-tax
// view so the user can see *exactly* how taxation is dragging their return.

import { Receipt, TrendingDown, TrendingUp, Info } from "lucide-react";
import { useFmt } from "@/lib/useFmt";

function Row({ label, pre, post, fmt, negative }) {
  return (
    <tr className="border-t border-slate-100">
      <td className="py-2.5 pr-3 text-sm text-slate-600">{label}</td>
      <td className="py-2.5 px-3 text-right text-sm tabular-nums text-slate-700">{fmt(pre)}</td>
      <td className={`py-2.5 pl-3 text-right text-sm tabular-nums font-semibold ${negative ? "text-red-600" : "text-slate-900"}`}>
        {fmt(post)}
      </td>
    </tr>
  );
}

export default function TaxImpactPanel({ deal }) {
  const fmt = useFmt();
  const rentalTaxRate = Number(deal.rental_income_tax_rate || 0);

  // If the country has no rental income tax (e.g. UAE), keep the panel quiet
  // but still useful — show the "good news" version.
  if (!rentalTaxRate) {
    return (
      <div
        data-testid="tax-impact-panel"
        className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 shadow-sm sm:p-8"
      >
        <div className="flex items-start gap-3">
          <Receipt className="mt-0.5 h-5 w-5 text-emerald-700" />
          <div>
            <h3 className="text-base font-semibold text-emerald-900">Tax-friendly jurisdiction</h3>
            <p className="mt-1 text-sm text-emerald-800/80">
              No effective rental income tax on this country's data sheet — your headline cash flow
              already reflects what hits your bank account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const drag = Number(deal.cash_flow_annual || 0) - Number(deal.cash_flow_after_tax_annual || 0);
  const dragPct = deal.cash_flow_annual ? (drag / Math.abs(deal.cash_flow_annual)) * 100 : 0;

  return (
    <div
      data-testid="tax-impact-panel"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              Tax impact
            </h3>
          </div>
          <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
            Rental income tax: <span className="text-indigo-700">{(rentalTaxRate * 100).toFixed(1)}%</span>
          </h2>
          <p className="mt-1 max-w-xl text-sm text-slate-500">
            Country-specific effective rate applied to your annual NOI. Headline numbers stay
            pre-tax; the after-tax column shows what survives.
          </p>
        </div>
        <div
          data-testid="tax-impact-drag"
          className={`min-w-[160px] rounded-xl border px-4 py-3 ${
            dragPct >= 30
              ? "border-red-200 bg-red-50"
              : dragPct >= 15
              ? "border-amber-200 bg-amber-50"
              : "border-slate-200 bg-slate-50"
          }`}
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Cash-flow drag
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <TrendingDown className="h-4 w-4 text-red-500" />
            <span className="text-2xl font-bold tracking-tight text-slate-900">
              {dragPct.toFixed(0)}%
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">{fmt.money(-drag, { cents: true })} / yr</p>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="pb-3 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Metric
              </th>
              <th className="pb-3 pr-3 text-right text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Pre-tax
              </th>
              <th className="pb-3 pl-3 text-right text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
                After tax
              </th>
            </tr>
          </thead>
          <tbody>
            <Row
              label="NOI (annual)"
              pre={deal.noi_annual}
              post={deal.noi_after_tax_annual}
              fmt={(v) => fmt.money(v, { cents: true })}
            />
            <Row
              label="Cash flow (annual)"
              pre={deal.cash_flow_annual}
              post={deal.cash_flow_after_tax_annual}
              fmt={(v) => fmt.money(v, { cents: true })}
              negative={deal.cash_flow_after_tax_annual < 0}
            />
            <Row
              label="Cash flow (monthly)"
              pre={deal.cash_flow_monthly}
              post={deal.cash_flow_after_tax_monthly}
              fmt={(v) => fmt.money(v, { cents: true })}
              negative={deal.cash_flow_after_tax_monthly < 0}
            />
            <tr className="border-t border-slate-100">
              <td className="py-2.5 pr-3 text-sm text-slate-600">Cash-on-cash return</td>
              <td className="py-2.5 px-3 text-right text-sm tabular-nums text-slate-700">
                {fmt.percent(deal.cash_on_cash)}
              </td>
              <td className="py-2.5 pl-3 text-right text-sm tabular-nums font-semibold text-slate-900">
                {fmt.percent(deal.cash_on_cash_after_tax)}
              </td>
            </tr>
            <tr className="border-t border-slate-100">
              <td className="py-2.5 pr-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1">
                  Tax paid (annual)
                </span>
              </td>
              <td className="py-2.5 px-3 text-right text-sm tabular-nums text-slate-400">—</td>
              <td className="py-2.5 pl-3 text-right text-sm tabular-nums font-semibold text-red-600">
                {fmt.money(deal.rental_income_tax_annual, { cents: true })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
        <p>
          Approximation only. Country rates use the static framework; actual tax depends on
          residency, marginal bracket, allowed deductions, and treaty positions. Consult a local
          tax professional.
        </p>
      </div>

      {deal.cash_on_cash_after_tax < 0 && deal.cash_on_cash >= 0 ? (
        <div
          data-testid="tax-flips-negative-warning"
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700"
        >
          <TrendingUp className="h-3.5 w-3.5 rotate-180" />
          After-tax return goes negative — the headline number is misleading for an investor in
          this country.
        </div>
      ) : null}
    </div>
  );
}
